/**
 * ExperimentDetailPage — 실험(Experiment) 단건 상세.
 *
 * 【역할】
 * - 실험 메타 + Runway/MLflow 최신 metrics 를 병합 조회 (enriched)
 * - Runway API 호출 실패 시 fallback 알림 표시
 *
 * 【주요 기능】
 * - URL :id 파라미터로 단건 조회
 * - Descriptions 컴포넌트로 메타 표시
 * - Runway metrics 조인 영역 (accuracy/loss/... 동적 key)
 * - Runway 호출 실패 시 Alert fallback
 * - 뒤로가기 버튼 → 목록
 *
 * 【사용 훅】
 * - useTransaction
 * - useParams / useNavigate
 * - useLabel (t)
 *
 * 【Backend RPC serviceName】
 * - experiment/searchByIdEnriched  — 단건 + Runway metrics 조인 조회
 *
 * 【DataSet 구조】
 * - 없음 (단건 응답을 state 로 직접 관리)
 *
 * 【사용처】 router/index.tsx — `{ path: 'experiments/:id' }`
 * 【포팅 원본】 react-fastapi 신규 화면 (Runway 연동 드릴다운)
 */
import { useEffect, useState } from 'react'
import { Card, Descriptions, Space, Tag, Typography, Button, Alert } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title, Text } = Typography

interface Experiment {
  experimentId: number
  name: string
  description?: string
  runwayRef?: string
  status: string
  metricsJson?: string
  tags?: string
  createdBy?: string
  createdAt?: string
  runwayMetrics?: Record<string, unknown> | null
}

/**
 * ExperimentDetailPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ExperimentDetailPage() {
  const { t } = useLabel()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { transaction } = useTransaction()
  const [exp, setExp] = useState<Experiment | null>(null)
  const [fallback, setFallback] = useState(false)

  useEffect(() => {
    const load = async () => {
      const res = await transaction({
        id: 'load',
        service: 'experiment/searchByIdEnriched',
        action: 'search',
        inds: { ds_search: { experimentId: Number(id) } }
      })
      const row = (res.data?.ds_experiment as { rows?: Experiment[] } | undefined)?.rows?.[0]
      if (row) {
        setExp(row)
        setFallback(Boolean(row.runwayRef) && !row.runwayMetrics)
      }
    }
    if (id) load()
  }, [id])

  if (!exp) return <PageContainer>{t('LBL_LOADING', '로딩 중...')}</PageContainer>

  let localMetrics: Record<string, unknown> = {}
  try {
    localMetrics = exp.metricsJson ? JSON.parse(exp.metricsJson) : {}
  } catch {
    /* ignore */
  }

  return (
    <PageContainer style={{ overflow: 'auto' }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {t('BTN_BACK', '뒤로')}
        </Button>
      </Space>

      <Title level={3} style={{ marginTop: 0 }}>
        {exp.name}
      </Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="ID">{exp.experimentId}</Descriptions.Item>
          <Descriptions.Item label={t('LBL_EXPERIMENT_STATUS', '상태')}>
            <Tag>{exp.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_RUNWAY_REF', 'Runway 참조')}>
            {exp.runwayRef || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_TAGS', '태그')}>{exp.tags || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('LBL_CREATED_BY', '생성자')}>
            {exp.createdBy || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_CREATED_AT', '생성일시')}>
            {exp.createdAt || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('LBL_DESCRIPTION', '설명')} span={2}>
            {exp.description || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title={t('LBL_LOCAL_METRICS', '로컬 메트릭')} style={{ marginBottom: 12 }}>
        {Object.keys(localMetrics).length === 0 ? (
          <Text type="secondary">-</Text>
        ) : (
          <Descriptions column={2} size="small">
            {Object.entries(localMetrics).map(([k, v]) => (
              <Descriptions.Item key={k} label={k}>
                {typeof v === 'number' ? v.toFixed(4) : String(v)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Card>

      <Card size="small" title={t('LBL_RUNWAY_METRICS', 'Runway 실시간 메트릭')}>
        {fallback && (
          <Alert
            type="warning"
            showIcon
            message={t('MSG_RUNWAY_FALLBACK', 'Runway 서비스 일시 불가 — 로컬 메트릭만 표시')}
            style={{ marginBottom: 8 }}
          />
        )}
        {exp.runwayMetrics ? (
          <pre style={{ background: '#fafafa', padding: 8, fontSize: 12, margin: 0 }}>
            {JSON.stringify(exp.runwayMetrics, null, 2)}
          </pre>
        ) : (
          <Text type="secondary">-</Text>
        )}
      </Card>
    </PageContainer>
  )
}
